
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "./use-user";
import { useNavigate } from "react-router-dom";

/**
 * Hook to verify and maintain user authentication state
 * @param redirectToAuth Whether to redirect to auth page if not authenticated
 * @returns Authentication state information
 */
export function useSessionAuth(redirectToAuth = false) {
  const { isLoggedIn, user, isLoading } = useUser();
  const [sessionVerified, setSessionVerified] = useState(false);
  const navigate = useNavigate();
  const sessionCheckIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    async function verifySession() {
      try {
        // First check local state
        if (isLoggedIn && user) {
          setSessionVerified(true);
          return;
        }
        
        // Then verify with Supabase directly if needed
        if (!isLoggedIn) {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Error verifying session:", error);
            setSessionVerified(false);
            
            if (redirectToAuth && !isLoading && navigate) {
              navigate("/auth", { replace: true });
            }
            return;
          }
          
          const isValid = !!session;
          setSessionVerified(isValid);
          
          // Redirect if needed and no session found
          if (redirectToAuth && !isValid && !isLoading && navigate) {
            navigate("/auth", { replace: true });
          }
        }
      } catch (error) {
        console.error("Error verifying session:", error);
        setSessionVerified(false);
        
        if (redirectToAuth && !isLoading && navigate) {
          navigate("/auth", { replace: true });
        }
      }
    }
    
    // Initial verification
    if (navigate) {
      verifySession();
    
      // Set up periodic verification
      sessionCheckIntervalRef.current = window.setInterval(() => {
        verifySession();
      }, 2 * 60 * 1000); // Check every 2 minutes
    }
    
    return () => {
      // Clean up interval on unmount
      if (sessionCheckIntervalRef.current !== null) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [isLoggedIn, user, isLoading, navigate, redirectToAuth]);

  return {
    userAuthenticated: isLoggedIn || sessionVerified,
    sessionVerified,
    isLoading
  };
}
